import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Bell, CirclePlus, ClipboardList, Compass, HandCoins, Home, LogOut, Menu, MessageCircle, Settings, UserRound } from "lucide-react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuthStore } from "../../features/auth/store";
import logoAsset from "../../assets/tupi-logo.png";
import { listConversations } from "../../features/chat/api";
import { getConversationListItems, normalizeConversation } from "../../features/chat/normalize";
import { listNotices } from "../../entities/notice/api";
import { IS_API_MODE } from "../../shared/config/runtime";
import NotificationsPopover from "../NotificationsPopover/NotificationsPopover";

export function Brand({ compact = false }) {
  return (
    <NavLink className="brand" to="/" aria-label="图π首页">
      <img className="brand-mark" src={logoAsset} alt="" />
      {!compact && <span className="brand-word">图π</span>}
    </NavLink>
  );
}

const navItems = [
  { to: "/", label: "首页", icon: Home, end: true },
  { to: "/explore", label: "发现", icon: Compass },
  { to: "/messages", label: "消息", icon: MessageCircle },
  { to: "/notifications", label: "通知", icon: Bell },
  { to: "/create", label: "创建", icon: CirclePlus },
  { to: "/grab-orders", label: "抢单", icon: HandCoins },
  { to: "/orders", label: "订单", icon: ClipboardList },
  { to: "/profile", label: "主页", icon: UserRound },
];
const sidebarExpandDelay = 80;

function Sidebar() {
  const [moreOpen, setMoreOpen] = useState(false);
  const [notificationOpen, setNotificationOpen] = useState(false);
  const [hoverExpanded, setHoverExpanded] = useState(false);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const moreRef = useRef(null);
  const notificationRef = useRef(null);
  const expandTimerRef = useRef(null);
  const clearSession = useAuthStore((state) => state.clearSession);
  const navigate = useNavigate();
  const location = useLocation();
  const conversationsQuery = useQuery({
    queryKey: ["chat", "conversations"],
    queryFn: listConversations,
    enabled: IS_API_MODE && isAuthenticated,
    staleTime: 0,
    refetchInterval: IS_API_MODE && isAuthenticated ? 5_000 : false,
  });
  const noticesQuery = useQuery({
    queryKey: ["notices", "unread"],
    queryFn: () => listNotices({ unreadOnly: true }),
    enabled: IS_API_MODE && isAuthenticated,
    staleTime: 30_000,
    refetchInterval: IS_API_MODE && isAuthenticated ? 30_000 : false,
  });

  const conversations = getConversationListItems(conversationsQuery.data).map(normalizeConversation);
  const notices = Array.isArray(noticesQuery.data)
    ? noticesQuery.data
    : noticesQuery.data?.results || [];
  const unreadMessages = conversations.reduce(
    (total, conversation) => total + Number(conversation.unread || 0),
    0,
  );
  const unreadNotices = notices.length;

  useEffect(() => {
    if (!moreOpen && !notificationOpen) return undefined;
    function handleOutsideClick(event) {
      if (!moreRef.current?.contains(event.target)) setMoreOpen(false);
      if (!notificationRef.current?.contains(event.target)) setNotificationOpen(false);
    }
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [moreOpen, notificationOpen]);

  useEffect(() => () => window.clearTimeout(expandTimerRef.current), []);

  function scheduleSidebarExpansion() {
    window.clearTimeout(expandTimerRef.current);
    expandTimerRef.current = window.setTimeout(() => {
      setHoverExpanded(true);
    }, sidebarExpandDelay);
  }

  function collapseSidebar() {
    window.clearTimeout(expandTimerRef.current);
    expandTimerRef.current = null;
    setHoverExpanded(false);
  }

  function logout() {
    clearSession();
    setMoreOpen(false);
    navigate("/login", { replace: true });
  }

  return (
    <aside
      className={`sidebar${hoverExpanded || moreOpen || notificationOpen ? " expanded" : ""}`}
      onMouseEnter={scheduleSidebarExpansion}
      onMouseLeave={collapseSidebar}
    >
      <Brand compact />
      <nav className="side-nav" aria-label="主导航">
        {navItems.map(({ to, label, icon: Icon, end }) => {
          const badge = to === "/messages" ? unreadMessages : to === "/notifications" ? unreadNotices : 0;
          if (to === "/notifications") {
            return (
              <div className="notification-nav-wrapper" key={to} ref={notificationRef}>
                <button
                  type="button"
                  className={`nav-item${notificationOpen || location.pathname.startsWith("/notifications") ? " active" : ""}`}
                  onClick={() => setNotificationOpen((open) => !open)}
                  aria-label="通知"
                  aria-expanded={notificationOpen}
                >
                  <span className="nav-icon-wrap">
                    <Icon size={24} strokeWidth={1.9} />
                    {badge ? <span className="nav-badge">{badge}</span> : null}
                  </span>
                  <span>{label}</span>
                </button>
                {notificationOpen && isAuthenticated ? <NotificationsPopover onClose={() => setNotificationOpen(false)} /> : null}
              </div>
            );
          }
          return (
          <NavLink
            key={to}
            to={to === "/profile" && !isAuthenticated ? "/login" : to}
            end={end}
            className={({ isActive }) => `nav-item${isActive ? " active" : ""}`}
          >
            <span className="nav-icon-wrap">
              <Icon size={24} strokeWidth={1.9} />
              {badge ? <span className="nav-badge">{badge}</span> : null}
            </span>
            <span>{to === "/profile" && !isAuthenticated ? "登录" : label}</span>
          </NavLink>
          );
        })}
      </nav>
      <div className="more-wrapper" ref={moreRef}>
        <button className="nav-item more-button" onClick={() => setMoreOpen((value) => !value)}>
          <Menu size={24} />
          <span>更多</span>
        </button>
        {moreOpen ? (
          <div className="more-menu">
            <NavLink to="/settings/profile" onClick={() => setMoreOpen(false)}>
              <Settings size={18} />
              设置
            </NavLink>
            {isAuthenticated ? (
              <button onClick={logout}>
                <LogOut size={18} />
                退出登录
              </button>
            ) : null}
          </div>
        ) : null}
      </div>
    </aside>
  );
}

function MobileHeader() {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const conversationsQuery = useQuery({
    queryKey: ["chat", "conversations"],
    queryFn: listConversations,
    enabled: IS_API_MODE && isAuthenticated,
    staleTime: 0,
    refetchInterval: IS_API_MODE && isAuthenticated ? 5_000 : false,
  });
  const noticesQuery = useQuery({
    queryKey: ["notices", "unread"],
    queryFn: () => listNotices({ unreadOnly: true }),
    enabled: IS_API_MODE && isAuthenticated,
    staleTime: 30_000,
    refetchInterval: IS_API_MODE && isAuthenticated ? 30_000 : false,
  });
  const notices = Array.isArray(noticesQuery.data)
    ? noticesQuery.data
    : noticesQuery.data?.results || [];
  const conversations = getConversationListItems(conversationsQuery.data).map(normalizeConversation);
  const unreadMessages = conversations.reduce(
    (total, conversation) => total + Number(conversation.unread || 0),
    0,
  );

  return (
    <header className="mobile-header">
      <Brand />
      <div className="mobile-actions">
        <NavLink to="/notifications" aria-label="通知">
          <Bell size={24} />
          {notices.length > 0 ? <span className="notification-dot" /> : null}
        </NavLink>
        <NavLink to="/messages" aria-label="消息">
          <MessageCircle size={25} />
          {unreadMessages ? <span className="nav-badge mobile-message-badge">{unreadMessages}</span> : null}
        </NavLink>
      </div>
    </header>
  );
}

function MobileNav() {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const items = navItems.filter(({ to }) =>
    ["/", "/explore", "/create", "/grab-orders", "/orders", "/profile"].includes(to),
  );
  return (
    <nav className="mobile-nav" aria-label="移动端主导航">
      {items.map(({ to, label, icon: Icon, end }) => (
        <NavLink key={to} to={to === "/profile" && !isAuthenticated ? "/login" : to} end={end} aria-label={to === "/profile" && !isAuthenticated ? "登录" : label}>
          {({ isActive }) => (
            <Icon size={25} fill={isActive && to === "/" ? "currentColor" : "none"} />
          )}
        </NavLink>
      ))}
    </nav>
  );
}

export default function AppShell({ children }) {
  const location = useLocation();
  const isMessages = location.pathname.startsWith("/messages");
  const isSettings = location.pathname.startsWith("/settings");
  return (
    <div className={`app-shell${isSettings ? " settings-shell" : ""}`}>
      <Sidebar />
      <MobileHeader />
      <main className={`app-main${isMessages ? " messages-main" : ""}`}>{children}</main>
      <MobileNav />
    </div>
  );
}
